import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatDate(date: Date | string): string {
  if (typeof date === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(date)) {
    // Already in dd/mm/yyyy format, just return it
    return date
  }
  
  const d = typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date) ? new Date(date + 'T00:00:00') : new Date(date)
  const day = String(d.getDate()).padStart(2, '0')
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const year = d.getFullYear()
  return `${day}/${month}/${year}`
}

export function formatISODate(date: Date | string): string {
  const d = typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date) ? new Date(date + 'T00:00:00') : new Date(date)
  return d.toISOString().split('T')[0]
}

export function parseDateToISO(dateStr: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    return dateStr
  }
  
  const [day, month, year] = dateStr.split('/').map(Number)
  const date = new Date(year, month - 1, day)
  return date.toISOString().split('T')[0]
}

export function formatDateTime(date: Date | string): string {
  const d = new Date(date)
  const day = String(d.getDate()).padStart(2, '0')
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const year = d.getFullYear()
  return `${day}/${month}/${year} ${formatTime(d)}`
}

export function formatTime(time: Date | string | null | undefined): string {
  if (!time) return '--:--'
  
  if (typeof time === 'string' && /^\d{2}:\d{2}(:\d{2})?$/.test(time)) {
    const [h, m] = time.split(':').map(Number)
    const period = h >= 12 ? 'PM' : 'AM'
    const hr = h % 12 || 12
    return `${hr}:${String(m).padStart(2, '0')} ${period}`
  }
  
  try {
    const d = new Date(time)
    if (isNaN(d.getTime())) return String(time)
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
  } catch {
    return String(time)
  }
}

export function getTodayDateString(): string {
  return new Date().toISOString().split('T')[0]
}

export function generateEmployeeId(count: number): string {
  return `EMP${String(count + 1).padStart(3, '0')}`
}

export function getStatusColor(status: string): string {
  const colors: Record<string, string> = {
    PRESENT: 'bg-green-500',
    ABSENT: 'bg-red-500',
    LEAVE: 'bg-yellow-500',
    PENDING: 'bg-yellow-500',
    APPROVED: 'bg-green-500',
    REJECTED: 'bg-red-500',
    PLANNING: 'bg-blue-500',
    ACTIVE: 'bg-green-500',
    ON_HOLD: 'bg-yellow-500',
    COMPLETED: 'bg-emerald-500',
    CANCELLED: 'bg-red-500',
    IN_PROGRESS: 'bg-blue-500',
    LOW: 'bg-gray-500',
    MEDIUM: 'bg-yellow-500',
    HIGH: 'bg-orange-500',
    CRITICAL: 'bg-red-500'
  }
  return colors[status] || 'bg-gray-500'
}
